import React, { useRef, MutableRefObject, useState, useEffect } from 'react';
import classNames from 'classnames';
import './OverlayBackdrop.less';
import { animated, useSpring } from 'react-spring';

export type OverlayClickPropagationSetting = 'right' | 'left' | 'both';

interface IOverlayBackdropProps {
  isOpen?: boolean;
  children?: React.ReactNode;
  /** Handler to close overlay on click outside contents */
  onClose: () => void;
  className?: string;
  isVisible?: boolean;
  /** Which mouse events, right, left or both, should we allowed to propagate from the overlay backdrop */
  allowEventPropagation?: OverlayClickPropagationSetting;
}

const getClassName = (isOpen?: boolean, additionalClasses?: string, isVisible?: boolean) =>
  classNames('dpc-overlay-backdrop', additionalClasses, {
    'dpc-overlay-backdrop--open': isOpen,
    'dpc-overlay-backdrop--visible': isOpen,
  });

/**
 * A backdrop / background component for callouts, overlays, and dropdowns.
 * This is primarily to create a space to lay these elements out, and catch mouse events, etc.
 * but could also be used for a darkened backdrop, etc.
 *
 * TODO if there's time, investigate whether we need this, or if it's something we could bake into the "useOverlayPortalTarget" hook.
 * or alternately, something like https://github.com/Pomax/react-onclickoutside, but I suspect that until the UI is a react app,
 * we'll have trouble keeping events to close overlays from also triggering jQuery events in the same click.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function OverlayBackdrop({
  allowEventPropagation: allowClickPropagation,
  children,
  isOpen,
  isVisible,
  onClose,
  className,
}: IOverlayBackdropProps) {
  const [opacity, setOpacity] = useState(0.01);

  // TODO, if we wanted to only have one of these visible, we could track them in a zustand store, e.g.
  // and hide the ones that are more than one level deep.
  // then we could also manage what escape key does when there are layered overlays / dialogs, etc.
  // the trick is what do we keep track of, the DOM element? onClose was my first idea, but then people have to remember to use "useCallback" :(
  useEffect(() => {
    if (isVisible) {
      setOpacity(isVisible ? 0.5 : 0.01);
    }
  }, [isVisible]);

  const styles = useSpring({
    opacity,
    config: {
      tension: 240,
    },
  });

  const backDropRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!allowPropagation(e, allowClickPropagation)) {
      e.stopPropagation();
    }
    // if the target element isn't the overlay itself, something else is going on
    if (e.target === backDropRef.current) {
      // TODO I'm pretty sure this was only necessary, because the backdrop was _wrapping_ the contents,
      // not _behind_ the contents.
      onClose();
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (!allowPropagation(e, allowClickPropagation)) {
      e.stopPropagation();
    }
    if (e.button === 2) {
      // Do we need things to stay open for right clicks? Probably not.
      if (e.target === backDropRef.current) {
        onClose();
      }
    }
  };

  const handleEsc = (e: React.KeyboardEvent) => {
    // TODO this would have worked before, if the callout contents had focus, and allowed events to bubble
    // this won't work if the backdrop is behind the contents.
    if (e.key === 'Escape') {
      onClose();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <animated.div
        // aria-hidden
        className={getClassName(isOpen, className, isVisible)}
        onMouseDown={onMouseDown}
        onClick={handleBackdropClick}
        ref={backDropRef}
        onKeyDown={handleEsc}
        style={styles}
      />
      {children}
    </>
  );
}

function allowPropagation(e: React.MouseEvent, setting?: OverlayClickPropagationSetting) {
  return (
    setting && (setting === 'both' || (setting === 'right' && e.button === 2) || (setting === 'left' && e.button === 1))
  );
}
